import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFormComponent,
  LocalesService,
  CoursesService,
  ProfileService,
  CourseDocumentsService,
  ItinerariesService
} from 'core/index';
import { ModalComponent } from 'core/components/modal/modal.component';
import { CoursesQuestionsService } from '../services/courses-questions.service';
import { CourseConfigurationService } from '../services/course-configuration.service';
import { BUTTON_ACTIONS, STYLE_FORM_SCOPES } from '../assets/constants';

@Component({
  selector: 'lms-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrls: ['./course-detail.component.scss']
})
export class CourseDetailComponent implements OnInit {
  @ViewChild('courseConfigForm', { static: true }) private courseConfigForm: DynamicFormComponent;
  public data: any = {};
  public queryClientId: string;
  public course: any;
  public languages: string;
  public itineraries: any;
  public isFormEmpty = true;
  private catalog: any;
  private configData: any = {};
  private configMetaData: any = {};
  public items: any[] = BUTTON_ACTIONS;
  public currentLang: any;
  public selectedLang: any;
  public translationsLangs: any = [];
  public itinerariesAffectedByWithdrawn = [];

  public hasCourseActions = false;
  public isScormClient = false;
  public canWithdrawCourse = false;
  public canReactivateCourse = false;

  @ViewChild('modalClientsPublishSelectorAlert', { static: false })
  public modalClientsPublishSelectorAlert: ModalComponent;
  @ViewChild('modalConfirmWithdrawCourse', { static: false })
  public modalConfirmWithdrawCourse: ModalComponent;
  @ViewChild('modalReactivateCourse', { static: false })
  public modalReactivateCourse: ModalComponent;
  @ViewChild('modalCourseDocuments', { static: false })
  public modalCourseDocuments: ModalComponent;

  public readonly styleFormScopes: Object = STYLE_FORM_SCOPES;

  constructor(
    public coursesQuestionsService: CoursesQuestionsService,
    public courseDocumentsService: CourseDocumentsService,
    public elementRef: ElementRef,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private localesService: LocalesService,
    private profileService: ProfileService,
    private courseConfigurationService: CourseConfigurationService,
    private coursesService: CoursesService,
    private itinerariesService: ItinerariesService,
    private translateService: TranslateService
  ) {}

  public ngOnInit() {
    this.activatedRoute.data.subscribe((data) => {
      this.course = data['course'];
      this.catalog = data['courses'];
      this.languages = this.localesService.getAvailableLocaleOptions(this.course.languages).reduce((acc, lang, idx) => {
        const value = acc + (idx > 0 ? ', ' : '') + lang.value;
        this.translationsLangs.push({ value: lang.key, label: lang.value });
        return value;
      }, '');
      this.queryClientId = data['client'] ? data['client']['id'] : null;
      this.isScormClient = data['client']?.scorm?.enabled;

      this.profileService.getLoggedUser$().subscribe((loggedUser) => {
        this.data = {
          course: this.course,
          defaultAccessMode: this.catalog.defaultAccessMode,
          clientId: this.queryClientId || loggedUser.clientId,
          clientDetail: !!this.catalog.defaultAccessMode,
          userRole: loggedUser.roleOptions,
          clientIds: loggedUser.clientIds
        };
      });
    });
    this.currentLang = this.translateService.currentLang;

    if (!this.course.withdrawn && (this.data.userRole.name === 'distributor' || this.data.userRole.name === 'expert')) {
      this.canWithdrawCourse = this.data.clientIds.includes(this.course.clientId);
    } else {
      this.canWithdrawCourse = !this.course.withdrawn;
    }

    if (this.course.withdrawn && (this.data.userRole.name === 'distributor' || this.data.userRole.name === 'expert')) {
      this.canReactivateCourse = this.data.clientIds.includes(this.course.clientId);
    } else {
      this.canReactivateCourse = this.course.withdrawn;
    }

    this.hasCourseActions =
      (this.queryClientId &&
        this.isScormClient &&
        (this.profileService.canExecute('courses.scorm') ||
          this.profileService.canExecute('courses.download-documents'))) ||
      (this.canWithdrawCourse && this.profileService.canExecute('courses.withdrawCourse'));
  }

  /**
   * Submit the course configuration
   * @param formData
   */
  public async submit(formData: any) {
    this.configData = this.parseFormData(formData['courseConfigIds']);
    this.configMetaData = formData['courseMetaIds'];

    await this.updateConfiguration(this.configData, this.configMetaData);
  }

  /**
   * Update the client modal
   */
  public async confirmUpdate() {
    const accessChangeConfirmed = true;
    await this.updateConfiguration(this.configData, null, accessChangeConfirmed);

    this.modalClientsPublishSelectorAlert.close();
  }

  /**
   * Save the course configuration
   * @param data
   * @param metaData
   * @param accessChangeConfirmed
   */
  private async updateConfiguration(data: any, metaData?: any, accessChangeConfirmed: boolean = false) {
    const submitAction = this.coursesQuestionsService.getSubmitAction();
    let newData: Object = null;
    let newMetaData: Object = null;
    data = {
      ...data,
      clientId: this.data.clientId,
      isClientConfig: this.data.clientDetail,
      catalogId: this.catalog.id,
      submitAction,
      accessChangeConfirmed
    };

    const updateCoursePromise: Promise<any> = this.courseConfigurationService.updateCourseConfiguration(
      data,
      this.course
    );

    let updateCourseMeta: Promise<any> = null;
    if (metaData && this.profileService.canExecute('courses.meta.edit')) {
      updateCourseMeta = this.updateCourseMetaInfo(metaData);
    }

    Promise.all([updateCoursePromise, updateCourseMeta])
      .then((data) => {
        newData = data[0];
        newMetaData = data[1];
        this.courseConfigForm.submitted(null);
      })
      .catch((error) => {
        if (error.responseStatusCode == 428) {
          // Open publish mode change confirmation modal with affected itineraries data
          this.itineraries = error.payload.itineraries;
          this.modalClientsPublishSelectorAlert.open();
        } else {
          this.courseConfigForm.submitted(error);
        }
      });

    this.course = {
      ...newMetaData,
      ...this.course,
      ...newData
    };
  }

  /**
   * Parses form data to turn Dynamic Question values into valid values.
   *
   * @param formData The form's data.
   */
  private parseFormData(formData: any) {
    const { bonusPossible, shouldSendDiploma, demoEnabled, credits } = formData;

    if (formData.publish && formData.publish.scope === '') {
      formData.publish.scope = null;
    }
    return {
      ...formData,
      bonusPossible: !!bonusPossible === bonusPossible ? bonusPossible : undefined,
      shouldSendDiploma: !!shouldSendDiploma === shouldSendDiploma ? shouldSendDiploma : undefined,
      demoEnabled: !!demoEnabled === demoEnabled ? demoEnabled : undefined,
      credits: credits ? Number(credits) : undefined
    };
  }

  /**
   * Save the course metadata
   * @param data the course information
   */
  public async updateCourseMetaInfo(data: any) {
    return await this.coursesService.updateCourseMeta(this.course.id, data);
  }

  /**
   * Cancel the form
   */
  public goBack(): void {
    this.location.back();
  }

  public onFormSet(data: any) {
    // Force property update to happen after the verification step so Angular
    // does not throw an ExpressionChangedAfterItHasBeenCheckedError at us
    // It works, but alternatives welcome for this isFormEmpty 'ñapa' 💁‍♀️
    Promise.resolve(null).then(() => {
      this.isFormEmpty = data.isFormEmpty;
    });
  }

  public selectLang(lang: any) {
    this.selectedLang = lang.detail.value;
  }

  /**
   * Download the course documents
   */
  public async downloadCourseDocuments(): Promise<any> {
    await this.courseDocumentsService
      .getCourseDocuments(this.course.id, this.selectedLang)
      .then((documents: string) => {
        this.courseDocumentsService.downloadDocumentation(documents, this.course.name, this.selectedLang);
        this.modalCourseDocuments.close();
      });
  }
  /**
   * Download the SCORM zip file for a course/class
   */
  public downloadScormFile(): void {
    this.coursesService.downloadScormCourse(this.course.id, this.queryClientId).then((zipFile: string) => {
      window.open(zipFile, '_self');
    });
  }

  public async onWithdrawCourse() {
    this.itinerariesAffectedByWithdrawn = await this.itinerariesService.getItinerariesByCourse(
      this.course.id,
      this.currentLang
    );
    this.modalConfirmWithdrawCourse.open();
  }

  public async toggleWithdrawCourse(value) {
    this.course = await this.coursesService.withdrawCourse(this.course.id, { withdrawn: value });
    this.modalConfirmWithdrawCourse.close();
    this.itinerariesAffectedByWithdrawn = [];
    this.modalReactivateCourse.close();
  }
}
